import { DocumentationChapter } from 'api/documentation';

import { chapterPath, DOCUMENTATION_ROOT } from '../utils';

/**
 * What the follow-along panel shows. Kept in memory only: a reload or a
 * close starts fresh, so a panel can never come back stuck on screen.
 */
export interface DocsPanelState {
  open: boolean;
  /** Docs page path, e.g. "onboarding/gcp". */
  path?: string;
  /** Section being read, so expanding returns to it. */
  anchor?: string;
  width: number;
  /** The app screen to return to when a page has no screen of its own. */
  lastAppLocation?: string;
}

export const DEFAULT_PANEL_WIDTH = 460;
export const MIN_PANEL_WIDTH = 360;
export const MAX_PANEL_WIDTH = 720;
/** Below this the panel would squeeze the app; a "Back to guide" chip is used. */
export const PANEL_MIN_SCREEN_WIDTH = 1100;

export const clampPanelWidth = (width: number, screenWidth: number): number =>
  Math.round(
    Math.min(
      Math.max(width, MIN_PANEL_WIDTH),
      Math.min(MAX_PANEL_WIDTH, screenWidth / 2),
    ),
  );

export const isDocumentationPath = (pathname: string): boolean =>
  pathname === DOCUMENTATION_ROOT ||
  pathname.startsWith(`${DOCUMENTATION_ROOT}/`);

/**
 * The app screen a page is about: its first route, the one the page's
 * "Follow along" opens. Prefix-only routes ("/setup/") aren't screens.
 */
export const primaryAppRoute = (
  chapter?: DocumentationChapter,
): string | undefined =>
  chapter?.routes?.find(route => route.startsWith('/') && !route.endsWith('/'));

const pathSegments = (path: string): string[] =>
  path.split(/[?#]/)[0].split('/').filter(Boolean);

/**
 * The docs page about an app screen, from the screens each page lists in
 * `routes`. The longest route that is the screen or one of its parents
 * wins, so /setup/accounts/add/gitlab finds the GitLab guide and an
 * unlisted /setup/accounts/add/oracle falls back to onboarding. Ties go to
 * a page named after a part of the URL ("integrations", "gitlab"), then to
 * the page that lists the route first, then to the shallower page.
 */
export const findChapterForAppRoute = (
  chapters: DocumentationChapter[],
  pathname: string,
): DocumentationChapter | undefined => {
  const segments = pathSegments(pathname);
  let best: { chapter: DocumentationChapter; score: number[] } | undefined;
  chapters.forEach(chapter => {
    const name = chapterPath(chapter).split('/').pop();
    const named = name && segments.includes(name) ? 1 : 0;
    (chapter.routes ?? []).forEach((route, index) => {
      const routeSegments = pathSegments(route);
      if (
        !routeSegments.length ||
        routeSegments.some((segment, i) => segment !== segments[i])
      )
        return;
      const score = [
        routeSegments.length,
        named,
        -index,
        -(chapter.depth ?? 0),
      ];
      // Compare left to right; the first difference decides.
      const i = best ? score.findIndex((v, j) => v !== best!.score[j]) : -1;
      const better = !best || (i !== -1 && score[i] > best.score[i]);
      if (better) best = { chapter, score };
    });
  });
  return best?.chapter;
};

/** URL of a docs page, optionally at a section. */
export const documentationUrl = (path: string, anchor?: string): string =>
  `${DOCUMENTATION_ROOT}/${path}${anchor ? `#${anchor}` : ''}`;

/**
 * A docs link's page and section. Links in the Markdown point at
 * /documentation/<path>[#anchor]; anything else isn't a docs page.
 */
export const parseDocumentationHref = (
  href: string,
): { path: string; anchor?: string } | undefined => {
  const [pathname, anchor] = href.split('#');
  const clean = pathname.split('?')[0].replace(/\/+$/, '');
  if (!clean.startsWith(`${DOCUMENTATION_ROOT}/`)) return undefined;
  const path = decodeURIComponent(clean.slice(DOCUMENTATION_ROOT.length + 1));
  return path ? { path, anchor: anchor || undefined } : undefined;
};

/** True for a click the browser should handle (new tab, new window, ...). */
export const isModifiedClick = (event: React.MouseEvent): boolean =>
  event.button !== 0 ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey;

/** Finds a heading inside the panel only, never one on the app's page. */
export const findInPanel = (
  container: HTMLElement | null,
  id: string,
): HTMLElement | null =>
  container?.querySelector<HTMLElement>(`[id="${CSS.escape(id)}"]`) ?? null;

/** Scrolls the panel (not the app) so a heading sits at its top. */
export const scrollPanelTo = (
  container: HTMLElement | null,
  id: string,
  smooth: boolean,
): boolean => {
  const heading = findInPanel(container, id);
  if (!container || !heading) return false;
  container.scrollTo({
    top:
      container.scrollTop +
      heading.getBoundingClientRect().top -
      container.getBoundingClientRect().top -
      8,
    behavior: smooth ? 'smooth' : 'auto',
  });
  return true;
};
